<template>
  <div class="mobile-search-select">
    <div class="select-area" @click="toShowDrawer">
      <span class="text" :class="{placeholder: !showText, small: size === 'small'}">{{ showText || placeholder }}</span>
    </div>
    <!--
      抽屉头部：清空 - 标题 - 确认。
      ★ 左边原本是「取消」，但点遮罩本身就是取消，这个按钮是多余的；
        原来放在内容区顶部的「清除项」挪到这里，操作位置不变、少占一行。
    -->
    <mobile-drawer v-model="showDrawer" :title="drawerTitle" cancel-text="清空"
                   @confirm="toConfirm" @cancel="toClear">
      <div class="select-data">
        <mobile-list-header>
          <div class="search-bar">
            <div class="search-input">
              <yq-icon class="icon" name="element-ui_search"/>
              <input v-model="searchKey" :placeholder="searchPlaceholder" @keyup.enter="resetSearch"/>
              <yq-icon v-if="searchKey" class="icon clear" name="element-ui_close" @click="clearKey"/>
            </div>
            <!--
              ★ 必须有这个显式的搜索按钮：手机上光靠 input 的回车不靠谱 ——
                中文输入法打字时按回车是「上屏」、被输入法自己吃掉，根本走不到 keyup.enter，
                用户就会觉得「输入了条件却没法查」。
            -->
            <div class="search-btn" @click="resetSearch">搜索</div>
          </div>
        </mobile-list-header>
        <mobile-list :loading="loading" :has-more="hasMore" :query-list="queryList">
          <div class="data-item" v-for="item in recordList" :key="item[codeColumn]" @click="selectRecord(item)">
            <!--
              行内容默认取 labelColumn 那一列；调用方可用 #item 插槽整行自定义
              （右侧那个「已选中」的勾仍由本组件渲染，插槽里不用重复画）。
              自定义内容的公共样式（两行文案、带头像那一款）见下方非 scoped 的 .mss-item-* 类。
            -->
            <slot name="item" :item="item">
              <div class="column-label">{{ item[labelColumn] }}</div>
            </slot>
            <yq-icon v-if="modelValue === item[codeColumn]" class="selected" name="element-ui_check"/>
          </div>
        </mobile-list>
      </div>
    </mobile-drawer>
  </div>
</template>

<script>

import YqIcon from "@/components/YqIcon.vue";
import MobileDrawer from "@/components/qywx/data/MobileDrawer.vue";
import MobileList from "@/components/qywx/layout/MobileList.vue";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";

export default {
  emits: ["update:modelValue", "change"],
  props: {
    modelValue: {default: ""},
    // 远程分页查询：(params) => Promise<{records, pageCount}>
    // params = {pageNumber, pageSize, searchKey}
    query: {
      type: Function,
      required: true
    },
    labelColumn: {default: "label"},          // 显示文案取哪个字段
    codeColumn: {default: "value"},           // 值取哪个字段
    placeholder: {default: "请选择"},          // 没有数据时显示（一般就是条件名，如「客户」）
    searchPlaceholder: {default: "请输入关键字"},
    title: {default: ""},                     // 抽屉标题，默认「选择 + placeholder」
    displayText: {default: ""},               // 外部回显文案（有值时优先用它）
    pageSize: {default: 20},
    size: {default: "normal"}, // 显示在数据位置的大小，normal正常，small小号
  },
  name: "MobileSearchSelect",
  components: {
    YqIcon, MobileDrawer, MobileList, MobileListHeader
  },
  data() {
    return {
      showDrawer: false,
      selectedLabel: "",   // 当前选中项的显示文案
      searchKey: "",
      loading: false,
      hasMore: true,
      recordList: [],
      pageNumber: 0,
    };
  },
  computed: {
    drawerTitle() {
      return this.title || (this.placeholder ? "选择" + this.placeholder : "选择");
    },
    showText() {
      return this.displayText || this.selectedLabel;
    }
  },
  methods: {
    // 打开抽屉：每次打开都重新查一遍，保证列表是新的
    toShowDrawer() {
      this.showDrawer = true;
      this.resetSearch();
    },
    resetSearch() {
      this.pageNumber = 0;
      this.recordList = [];
      this.hasMore = true;
      this.queryList();
    },
    clearKey() {
      this.searchKey = "";
      this.resetSearch();
    },
    queryList() {
      this.loading = true;
      this.pageNumber++;
      this.query({
        pageNumber: this.pageNumber,
        pageSize: this.pageSize,
        searchKey: this.searchKey,
      }).then(res => {
        this.recordList = this.recordList.concat(res.records || []);
        this.hasMore = res.pageCount > this.pageNumber;
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      })
    },
    // 选中一条：立即回传并关闭
    selectRecord(record) {
      const value = record[this.codeColumn];
      this.selectedLabel = record[this.labelColumn];
      this.$emit("update:modelValue", value);
      this.showDrawer = false;
      if (value !== this.modelValue) {
        this.$emit("change", record);
      }
    },
    // 清除
    toClear() {
      const hadValue = this.modelValue !== undefined && this.modelValue !== "";
      this.selectedLabel = "";
      this.$emit("update:modelValue", "");
      this.showDrawer = false;
      if (hadValue) {
        this.$emit("change", null);
      }
    },
    toConfirm() {
      this.showDrawer = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.mobile-search-select{
  // 触发区：纯文字、无背景色（和 MobileConditionSelect 保持一致）
  .select-area{
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    color: var(--yq-font-color-base);
    .text{
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      &.placeholder{
        color: var(--yq-font-color-desc);
      }
      &.small{
        font-size: 12px;
      }
    }
  }
}
.select-data{
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 0 10px;
  :deep(.mobile-list-header){
    flex: none;
  }
  .data-item{
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--el-border-color);
    font-size: var(--yq-font-size-large-mobile);
    line-height: 50px;
    color: var(--yq-font-color-tips);
    .column-label{
      flex: 1;
      min-width: 0;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .selected{
      flex: none;
      color: var(--el-color-success);
    }
  }
  // 搜索行：左边输入框、右边「搜索」按钮，与列表页的搜索栏同一套观感
  .search-bar{
    display: flex;
    align-items: center;
    height: 44px;
    padding-left: 12px;
    border-radius: 999px;
    background: var(--yq-background-color);
    overflow: hidden;
    .search-input{
      flex: 1;
      display: flex;
      align-items: center;
      min-width: 0;
      height: 100%;
      padding-right: 12px;
      .icon{
        flex: none;
        color: var(--yq-font-color-desc);
        font-size: var(--yq-font-size-mini-mobile);
        &.clear{
          margin-left: 6px;
        }
      }
      input{
        flex: 1;
        min-width: 0;
        height: 100%;
        margin-left: 6px;
        border: none;
        outline: none;
        background: transparent;
        font-size: var(--yq-font-size-mini-mobile);
        color: var(--yq-font-color-base);
        &::placeholder{
          color: var(--yq-font-color-desc);
        }
      }
    }
    .search-btn{
      flex: none;
      display: flex;
      align-items: center;
      height: 100%;
      padding: 0 18px;
      background: var(--el-color-primary);
      color: #FFF;
      font-size: var(--yq-font-size-mini-mobile);
    }
  }
}
</style>

<!--
  #item 插槽内容的公共样式。
  ★ 必须放在非 scoped 的块里：插槽内容是父组件的模板渲染出来的、带的是父组件的 scope 标记，
    子组件的 scoped 样式命中不了它；写在这里全局一份，各页面就不用各自抄一遍。
  ★ 类名统一带 mss-item 前缀（MSS = MobileSearchSelect），避免和业务样式撞车。
-->
<style lang="scss">
/* 两行文案：主行 + 浅色小字的副行（如客户名称 + 联系电话） */
.mss-item{
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 6px 0;
  line-height: 1.2;
}
.mss-item-main{
  font-size: var(--yq-font-size-mini-mobile);
  color: var(--yq-font-color-base);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mss-item-sub{
  margin-top: 4px;
  font-size: 12px;
  color: var(--yq-font-color-desc);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* 带头像的那一款（预览模板 B：头像 + 名称/手机号两行） */
.mss-item-avatar-row{
  flex-direction: row;
  align-items: center;
  gap: 10px;
}
.mss-item-avatar{
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: var(--yq-background-color);
  color: #b9c2cf;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  img{
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
.mss-item-info{
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
</style>
